import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtEUR } from "@/lib/format";
import { metKeys, nieuweRijKey } from "@/lib/rijkeys";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  UploadCloud, FileText, Loader2, AlertTriangle, CheckCircle2,
  Trash2, Plus, RotateCcw, PackageCheck, Sparkles, Tag, Star,
} from "lucide-react";
import { toast } from "sonner";
import { BtwOverzicht } from "@/components/BtwOverzicht";

const ACCEPT = ".pdf,.txt,.csv,.xlsx,.xlsm";
const EXTS = ["pdf", "txt", "csv", "xlsx", "xlsm"];

function Dropzone({ onFile, busy }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const handle = (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!EXTS.includes(ext)) { toast.error("Gebruik PDF, TXT, CSV of Excel (.xlsx)"); return; }
    onFile(file);
  };

  return (
    <div
      data-testid="order-dropzone"
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files?.[0]); }}
      onClick={() => !busy && inputRef.current?.click()}
      className={`relative cursor-pointer rounded-2xl border-2 border-dashed p-12 text-center transition-colors
        ${drag ? "border-accent bg-accent/5" : "border-border hover:border-accent/60 hover:bg-secondary/40"}`}
    >
      <input ref={inputRef} type="file" accept={ACCEPT} className="hidden"
        data-testid="order-file-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handle(f); }} />
      <div className="flex flex-col items-center gap-3">
        {busy ? (
          <Loader2 className="h-10 w-10 text-accent animate-spin" />
        ) : (
          <div className="h-14 w-14 rounded-2xl bg-accent/10 flex items-center justify-center">
            <UploadCloud className="h-7 w-7 text-accent" />
          </div>
        )}
        <div className="font-medium text-lg">
          {busy ? "Bestand wordt gelezen door AI…" : "Sleep een order hierheen"}
        </div>
        <div className="text-sm text-muted-foreground">
          {busy ? "Even geduld, dit kan een paar seconden duren." : "of klik om te bladeren · PDF, TXT, CSV, Excel"}
        </div>
      </div>
    </div>
  );
}

export default function InkomendeOrders() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [orgId, setOrgId] = useState("");
  const [orgFilter, setOrgFilter] = useState("");
  const [regels, setRegels] = useState([]);
  const [ordernummer, setOrdernummer] = useState("");
  const [leveranciersId, setLeveranciersId] = useState("");
  const [fileName, setFileName] = useState("");
  const [btw, setBtw] = useState(null);

  useEffect(() => {
    api.get("/organisaties").then(({ data }) => setOrgs(data || [])).catch(() => {});
  }, []);

  // Live BTW-berekening op basis van land van de gekozen organisatie
  useEffect(() => {
    if (!preview || !orgId) { setBtw(null); return; }
    const land = orgs.find((o) => o.id === orgId)?.land || "Nederland";
    const geldig = regels.filter((r) => (r.product || "").trim() && Number(r.aantal) > 0);
    if (geldig.length === 0) { setBtw(null); return; }
    const t = setTimeout(() => {
      api.post("/btw/bereken", {
        land,
        regels: geldig.map((r) => ({
          code: r.code || "", product: r.product, aantal: Number(r.aantal) || 0,
          verkoopprijs: Number(r.verkoopprijs) || 0,
        })),
      }).then(({ data }) => setBtw(data)).catch(() => setBtw(null));
    }, 450);
    return () => clearTimeout(t);
  }, [preview, orgId, regels, orgs]);

  const onFile = async (file) => {
    setFileName(file.name);
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/inkomende-orders/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setOrgId(data.organisatie?.id || "");
      setOrdernummer(data.ordernummer || "");
      setLeveranciersId(data.leveranciers_id || "");
      setRegels(metKeys(data.regels));
      if (!data.organisatie_gevonden) {
        toast.warning("Organisatie niet automatisch gevonden — kies handmatig.");
      } else {
        toast.success(`Organisatie herkend: ${data.organisatie.naam}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verwerken mislukt");
    } finally { setBusy(false); }
  };

  const updateRegel = (i, key, val) => {
    setRegels((rs) => rs.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  };
  const applySuggestie = (i, s) => {
    setRegels((rs) => rs.map((r, idx) => (idx === i ? {
      ...r, gevonden: true, match_via: "naam",
      code: s.code, product: s.product, productgroep: s.productgroep,
      verkoopprijs: s.verkoopprijs, inkoopprijs: s.inkoopprijs, is_klantprijs: s.is_klantprijs,
    } : r)));
  };
  const removeRegel = (i) => setRegels((rs) => rs.filter((_, idx) => idx !== i));
  const addRegel = () => setRegels((rs) => [...rs, {
    gevonden: false, code: "", product: "", productgroep: "", aantal: 1, verkoopprijs: 0, inkoopprijs: 0, suggesties: [], _key: nieuweRijKey(),
  }]);

  const reset = () => { setPreview(null); setRegels([]); setOrgId(""); setFileName(""); setOrgFilter(""); setOrdernummer(""); setLeveranciersId(""); };

  const totaal = regels.reduce((s, r) => s + (Number(r.aantal) || 0) * (Number(r.verkoopprijs) || 0), 0);
  const filteredOrgs = orgFilter
    ? orgs.filter((o) => (o.naam || "").toLowerCase().includes(orgFilter.toLowerCase()))
    : orgs;

  const submit = async () => {
    if (!orgId) { toast.error("Kies eerst een organisatie"); return; }
    const valid = regels.filter((r) => (r.product || "").trim() && Number(r.aantal) > 0);
    if (valid.length === 0) { toast.error("Voeg minimaal één regel met aantal toe"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/inkomende-orders/aanmaken", {
        relatie_id: orgId,
        ordernummer: ordernummer || "",
        leveranciers_id: leveranciersId || "",
        regels: valid.map((r) => ({
          code: r.code || "", product: r.product, productgroep: r.productgroep || "",
          aantal: Number(r.aantal) || 0, verkoopprijs: Number(r.verkoopprijs) || 0,
          inkoopprijs: Number(r.inkoopprijs) || 0,
        })),
      });
      toast.success("Verkoopkans aangemaakt");
      navigate(`/verkoopkansen/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aanmaken mislukt");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="inkomende-orders-page">
      <div>
        <div className="text-sm text-muted-foreground">Automatisering</div>
        <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Orderemmer</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Sleep een binnengekomen order (PDF, TXT, CSV of Excel) hierheen. De AI herkent de organisatie
          en de bestelde artikelen en maakt automatisch een verkoopkans met fase
          <span className="font-medium text-foreground"> "Inbound repeat order"</span>.
        </p>
      </div>

      {!preview && <Dropzone onFile={onFile} busy={busy} />}

      {preview && (
        <div className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="h-4 w-4" /> {fileName}
            </div>
            <Button variant="ghost" size="sm" onClick={reset} data-testid="order-reset-btn">
              <RotateCcw className="h-4 w-4 mr-1" /> Ander bestand
            </Button>
          </div>

          {/* Organisatie */}
          <div className="bg-card border border-border rounded-xl p-5 space-y-3" data-testid="order-org-section">
            <div className="font-medium">Organisatie</div>
            {!preview.organisatie_gevonden && (
              <div className="flex items-start gap-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-700 px-3 py-2 text-sm"
                   data-testid="order-org-warning">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  Organisatie <strong>niet gevonden</strong>
                  {preview.organisatie_match_term ? <> voor "{preview.organisatie_match_term}"</> : null}.
                  {preview.organisatie_suggesties?.length ? " Kies een suggestie of selecteer handmatig." : " Kies hieronder handmatig de juiste organisatie."}
                </span>
              </div>
            )}
            {!preview.organisatie_gevonden && preview.organisatie_suggesties?.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5" data-testid="order-org-suggesties">
                <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <Sparkles className="h-3 w-3 text-sky-500" /> Suggesties:
                </span>
                {preview.organisatie_suggesties.map((s) => (
                  <button key={s.id} type="button" onClick={() => setOrgId(s.id)}
                    data-testid={`order-org-suggestie-${s.id}`}
                    className={`text-[11px] rounded-full border px-2 py-0.5 transition-colors inline-flex items-center gap-1 ${
                      orgId === s.id ? "border-sky-400 bg-sky-50 text-sky-700"
                                     : "border-border hover:border-sky-300 hover:bg-sky-50/60 text-foreground"}`}>
                    {s.is_top_klant && <Star className="h-3 w-3 text-amber-500" />}
                    {s.naam}{s.plaats ? ` · ${s.plaats}` : ""}
                    <span className="text-muted-foreground">{Math.round(s.score * 100)}%</span>
                  </button>
                ))}
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <Input placeholder="Zoek organisatie…" value={orgFilter}
                onChange={(e) => setOrgFilter(e.target.value)} data-testid="order-org-filter" />
              <select
                data-testid="order-org-select"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
                className="h-10 rounded-md border border-border bg-background px-3 text-sm"
              >
                <option value="">— Kies organisatie —</option>
                {filteredOrgs.map((o) => (
                  <option key={o.id} value={o.id}>{o.naam}{o.plaats ? ` · ${o.plaats}` : ""}</option>
                ))}
              </select>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Ordernummer</label>
                <Input placeholder="Order-/bestelnummer" value={ordernummer}
                  onChange={(e) => setOrdernummer(e.target.value)} data-testid="order-ordernummer" />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Leveranciers-ID</label>
                <Input placeholder="Leveranciers-ID / relatienummer" value={leveranciersId}
                  onChange={(e) => setLeveranciersId(e.target.value)} data-testid="order-leveranciers-id" />
              </div>
            </div>
          </div>

          {/* Regels */}
          <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="order-regels-section">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <div className="font-medium">Orderregels ({regels.length})</div>
              <Button variant="outline" size="sm" onClick={addRegel} data-testid="order-add-regel">
                <Plus className="h-4 w-4 mr-1" /> Regel
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-muted-foreground text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left font-medium px-4 py-2 w-28">Status</th>
                    <th className="text-left font-medium px-4 py-2 w-36">Artikelcode</th>
                    <th className="text-left font-medium px-4 py-2">Product</th>
                    <th className="text-right font-medium px-4 py-2 w-24">Aantal</th>
                    <th className="text-right font-medium px-4 py-2 w-32">Prijs/st.</th>
                    <th className="text-right font-medium px-4 py-2 w-32">Regeltotaal</th>
                    <th className="px-2 py-2 w-10" />
                  </tr>
                </thead>
                <tbody>
                  {regels.map((r, i) => (
                    <tr key={r._key} className="border-t border-border" data-testid={`order-regel-${i}`}>
                      <td className="px-4 py-2">
                        {r.gevonden ? (
                          r.match_via === "naam" ? (
                            <Badge variant="outline" className="text-sky-600 border-sky-300 gap-1" data-testid={`order-regel-status-${i}`}>
                              <Sparkles className="h-3 w-3" /> AI-match
                            </Badge>
                          ) : r.match_via === "klantcode" ? (
                            <Badge variant="outline" className="text-emerald-600 border-emerald-300 gap-1" data-testid={`order-regel-status-${i}`}>
                              <Tag className="h-3 w-3" /> Klantcode
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-emerald-600 border-emerald-300 gap-1" data-testid={`order-regel-status-${i}`}>
                              <CheckCircle2 className="h-3 w-3" /> Gevonden
                            </Badge>
                          )
                        ) : (
                          <Badge variant="outline" className="text-amber-600 border-amber-300 gap-1" data-testid={`order-regel-status-${i}`}>
                            <AlertTriangle className="h-3 w-3" /> Onbekend
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <Input className="h-8" value={r.code || ""}
                          onChange={(e) => updateRegel(i, "code", e.target.value)}
                          data-testid={`order-regel-code-${i}`} />
                      </td>
                      <td className="px-4 py-2">
                        <Input className="h-8" value={r.product || ""}
                          onChange={(e) => updateRegel(i, "product", e.target.value)}
                          data-testid={`order-regel-product-${i}`} />
                        {r.suggesties?.length > 0 && (!r.gevonden || r.match_via === "naam") && (
                          <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid={`order-regel-suggesties-${i}`}>
                            <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                              <Sparkles className="h-3 w-3 text-sky-500" /> Suggesties:
                            </span>
                            {r.suggesties.map((s, si) => {
                              const actief = (r.code || "") === s.code;
                              return (
                                <button key={si} type="button" onClick={() => applySuggestie(i, s)}
                                  data-testid={`order-regel-suggestie-${i}-${si}`}
                                  className={`text-[11px] rounded-full border px-2 py-0.5 transition-colors ${
                                    actief ? "border-sky-400 bg-sky-50 text-sky-700"
                                           : "border-border hover:border-sky-300 hover:bg-sky-50/60 text-foreground"}`}>
                                  <span className="font-mono">{s.code}</span> · {s.product}
                                  <span className="text-muted-foreground ml-1">{Math.round(s.score * 100)}%</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <Input type="number" min="0" className="h-8 text-right" value={r.aantal}
                          onChange={(e) => updateRegel(i, "aantal", e.target.value)}
                          data-testid={`order-regel-aantal-${i}`} />
                      </td>
                      <td className="px-4 py-2">
                        <Input type="number" min="0" step="0.01" className="h-8 text-right" value={r.verkoopprijs}
                          onChange={(e) => updateRegel(i, "verkoopprijs", e.target.value)}
                          data-testid={`order-regel-prijs-${i}`} />
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {fmtEUR((Number(r.aantal) || 0) * (Number(r.verkoopprijs) || 0))}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <button onClick={() => removeRegel(i)} className="text-muted-foreground hover:text-red-600"
                          data-testid={`order-regel-del-${i}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {regels.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Geen regels herkend.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-5 py-3 border-t border-border">
              <div className="text-xs text-muted-foreground">
                Prijzen volgen klantspecifieke prijzen, anders de standaard artikelprijs.
              </div>
              <div className="text-sm font-medium" data-testid="order-totaal">Totaal: {fmtEUR(totaal)}</div>
            </div>
          </div>

          {btw && (
            <div className="bg-card border border-border rounded-xl p-5 max-w-md" data-testid="order-btw">
              <BtwOverzicht result={btw} />
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={reset}>Annuleren</Button>
            <Button onClick={submit} disabled={saving || !orgId} data-testid="order-submit-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <PackageCheck className="h-4 w-4 mr-1" />}
              Verkoopkans aanmaken
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
